import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../../Apiurl";
import { resolveTranslationUsername, translationErrorMessage, translationErrorProduct } from "../../traduzioni/translationRequest";
import { loadTranslationQuota, translationAllowed } from "../../traduzioni/translationQuota";
import { formatCaratteri, loadTranslationChannels, ordineCascata, partiQuota } from "../../traduzioni/translationChannels";
import { TextSkeleton } from "../../interfaccia/loading";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import "./Traduzioni.css";

const CAMPI = [
  ["titolo", "Titolo"], ["descBreve", "Descrizione breve"],
  ["descLunga", "Descrizione lunga"], ["descIntro", "Testo intro"],
];
const LINGUE = { en: "Inglese", fr: "Francese", es: "Spagnolo" };

// Testi invariati dopo la chiamata. Con "Traduci" (mancanti) l'API non ha
// toccato nulla; con "Ritraduci tutti" ha ritradotto e il risultato e' uguale,
// per esempio dalla cache o con lo stesso glossario.
export function avvisoInvariato(modalita, lingua, prodotto) {
  const nome = LINGUE[lingua] || lingua;
  if (modalita !== "tutti") {
    return `Nessun campo da tradurre in ${nome}: i campi sono già tradotti, modificati a mano o senza testo italiano.`;
  }
  const meta = CAMPI.map(([campo]) => prodotto?.traduzioniAuto?.[campo]?.[lingua]).filter(Boolean);
  const versioni = Array.from(new Set(meta.map((item) => item.versioneGlossario).filter(Boolean).map((v) => String(v).slice(0, 8))));
  const dettagli = [versioni.length ? `glossario ${versioni.join(", ")}` : "", meta.some((item) => item.origine === "cache") ? "in parte dalla cache" : ""]
    .filter(Boolean).join(", ");
  return `Ritraduzione in ${nome} eseguita: il testo è uguale al precedente${dettagli ? ` (${dettagli})` : ""}.`;
}
const STATI = {
  PRONTO: "Pronto", MANCANTE: "Mancante", PARZIALE: "Parziale", DA_AGGIORNARE: "Da aggiornare",
  NON_VERIFICATO: "Non verificato", ASSENTE: "Assente", SEGNAPOSTO: "Segnaposto", PRESENTE: "Presente",
  ALLINEATO: "Allineato", INVIATO_NON_VERIFICATO: "Inviato, non verificato", ERRORE_LETTURA: "Errore di lettura",
};

// Sezione di sola consultazione: italiano e traduzione affiancati, con HTML
// formattato. Le traduzioni si modificano dal tab "Descrizioni".
function TestoFormattato({ campo, testo, vuoto }) {
  if (!testo) return <p className="gray">{vuoto}</p>;
  if (campo === "titolo") return <p className="b">{testo}</p>;
  return <div className="traduzioniHtml" dangerouslySetInnerHTML={{ __html: testo }} />;
}

export default function Traduzioni({ prodotto, utente, salvato, tradPermessa, siti, refreshKey, onTranslated, onBusy, buttonClass }) {
  const { getAccessTokenSilently, user } = useAuth0();
  const [lingua, setLingua] = useState("en");
  const [stato, setStato] = useState(null);
  const [loading, setLoading] = useState(true);
  // Solo informativa: la quota la decide il server sui testi da tradurre davvero
  // (gli hit di cache non consumano), quindi non blocca il pulsante (#554).
  const [quotaEsaurita, setQuotaEsaurita] = useState(false);
  const [erroreStato, setErroreStato] = useState("");
  const [errore, setErrore] = useState("");
  // Errore dell'ultima traduzione: sopravvive al ricaricamento di stato e quota
  // che segue un salvataggio parziale.
  const [erroreTraduzione, setErroreTraduzione] = useState("");
  const [avviso, setAvviso] = useState("");
  const [busy, setBusy] = useState(false);
  const [conferma, setConferma] = useState(false);
  const [refresh, setRefresh] = useState(0);
  // T27: servizio scelto (automatico = cascata) e quote per canale.
  const [servizio, setServizio] = useState("auto");
  const [quote, setQuote] = useState(null);
  const pending = useRef(false);
  const mounted = useRef(true);
  const canRead = Boolean(utente?.permessiAPI?.["read:database"]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setErroreTraduzione(""); setAvviso(""); }, [prodotto.codiceVS]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setStato(null);
    setErroreStato("");
    setErrore("");
    setQuotaEsaurita(false);
    if (!prodotto.codiceVS || !canRead) {
      setErroreStato("Stato e quota non disponibili: servono un codice VS e il permesso read:database.");
      setLoading(false);
      return () => controller.abort();
    }
    async function leggi() {
      const results = await Promise.allSettled([
        (async () => {
          const token = await getAccessTokenSilently();
          if (controller.signal.aborted) return;
          return axios.get(`${apiUrl}/prodotti/translate/stato/${encodeURIComponent(prodotto.codiceVS)}`,
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
        })(),
        loadTranslationQuota(getAccessTokenSilently, controller.signal),
        loadTranslationChannels(getAccessTokenSilently, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      const [copertura, quota, canali] = results;
      setQuote(canali.status === "fulfilled" ? canali.value : null);
      if (copertura.status === "fulfilled" && Array.isArray(copertura.value?.data?.rows) &&
          Array.isArray(copertura.value?.data?.lingue) && Array.isArray(copertura.value?.data?.campi)) {
        setStato(copertura.value.data);
      } else {
        setErroreStato(copertura.status === "rejected" ? translationErrorMessage(copertura.reason) : "Stato traduzioni non disponibile.");
      }
      if (quota.status === "fulfilled") setQuotaEsaurita(!translationAllowed(quota.value.total, quota.value.limit));
      else setErrore(translationErrorMessage(quota.reason));
      setLoading(false);
    }
    leggi();
    return () => controller.abort();
  }, [prodotto.codiceVS, getAccessTokenSilently, canRead, refresh, refreshKey]);

  // Un servizio non piu' disponibile (chiave tolta, quota a zero) torna all'automatico.
  useEffect(() => {
    if (servizio !== "auto" && quote && !quote.servizi?.some((item) => item.id === servizio && item.disponibile)) setServizio("auto");
  }, [quote, servizio]);

  const lingue = stato?.lingue?.length ? stato.lingue : ["en", "fr"];
  const selezionata = lingue.includes(lingua) ? lingua : lingue[0];
  const motivo = stato?.soloItalia
    ? `Il brand ${stato.soloItalia.descrizione || stato.soloItalia.brand} non è venduto all'estero: la traduzione non serve`
    : busy ? "Traduzione in corso." : !salvato ? "Salva prima le modifiche: verranno tradotti i testi italiani salvati."
    : !prodotto._id ? "Salva il prodotto prima di tradurlo." : !utente?.permessiAPI?.["usage:googleapis"]
      ? "Serve il permesso usage:googleapis." : loading ? "Verifica della quota in corso." : "";
  const avvisoQuota = !motivo && (quotaEsaurita || tradPermessa === false)
    ? "Quota mensile esaurita o non configurata: si possono ottenere solo i testi già tradotti in cache, gli altri verranno rifiutati."
    : "";
  const campiConSorgente = CAMPI.filter(([campo]) => {
    const raw = String(prodotto.descrizioniSuPS?.[campo]?.it ?? "").trim();
    const presente = Boolean(raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim() || /<img\b/i.test(raw));
    return presente && stato?.campi.some((item) => item.campo === campo && item.siti.some((site) => siti.includes(site)));
  });

  async function traduci(modalita) {
    if (pending.current || motivo) return;
    pending.current = true;
    setBusy(true);
    onBusy(true);
    setErroreTraduzione("");
    setAvviso("");
    setConferma(false);
    const prima = CAMPI.map(([campo]) => prodotto.descrizioniSuPS?.[campo]?.[selezionata] ?? "");
    try {
      const token = await getAccessTokenSilently();
      const username = await resolveTranslationUsername({ username: utente?.username, email: user?.email, accessToken: token });
      const response = await axios.patch(`${apiUrl}/prodotti/translate/blindly`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { idDocProdotto: prodotto._id, username, lingue: selezionata, modalita, servizio },
      });
      if (mounted.current) {
        const dopo = CAMPI.map(([campo]) => response.data?.descrizioniSuPS?.[campo]?.[selezionata] ?? "");
        if (dopo.every((testo, index) => testo === prima[index])) setAvviso(avvisoInvariato(modalita, selezionata, response.data));
        onTranslated(response.data);
        setRefresh((value) => value + 1);
      }
    } catch (error) {
      if (mounted.current) {
        setErroreTraduzione(translationErrorMessage(error));
        const parziale = translationErrorProduct(error);
        if (parziale) {
          onTranslated(parziale);
          setRefresh((value) => value + 1);
        }
      }
    } finally {
      pending.current = false;
      onBusy(false);
      if (mounted.current) setBusy(false);
    }
  }

  function statiCampo(campo) {
    const siti = stato?.campi?.find((item) => item.campo === campo)?.siti || [];
    return <>
      <span className="f6">{siti.length === 2 ? "VIG e BOT" : siti.length ? `solo ${siti[0]}` : "Siti non disponibili"}</span>
      {siti.map((fonte) => {
        const row = stato.rows.find((item) => item.fonte === fonte && item.lingua === selezionata);
        const mancanti = String(row?.campi_mancanti || "").split(",");
        return <div key={fonte} className="f6 mt1">{fonte}: Domino {row ? mancanti.includes(campo) ? "Mancante" : STATI[row.stato_domino] || row.stato_domino : "Non calcolato"}
          {" · "}Online {row ? STATI[row.stato_online] || row.stato_online : "Non verificato"}
          {Boolean(row?.copia_it) && " · Descrizioni con copia italiano"}</div>;
      })}
    </>;
  }

  const nomeLingua = LINGUE[selezionata] || selezionata;
  // Barra fissa: la lingua e' un selettore (cosa vedo), "Traduci" l'azione
  // principale (cosa faccio). Il contenuto scorre sotto.
  return <section className="modificaProdottiPagina modificaProdottiTraduzioniPagina" aria-label="Traduzioni prodotto" aria-busy={busy}>
    <div className="traduzioniBarra pa2">
      <div className="traduzioniBarraRiga">
        <div className="traduzioniLingueGruppo">
          <span className="f6 gray mr2" id="traduzioni-lingua-label">Lingua</span>
          <nav aria-labelledby="traduzioni-lingua-label" className="traduzioniLingue">
            {lingue.map((target) => <button key={target} type="button"
              className={`traduzioniLingua${selezionata === target ? " traduzioniLinguaAttiva" : ""}`}
              aria-pressed={selezionata === target} disabled={busy} onClick={() => { setLingua(target); setConferma(false); setAvviso(""); }}>
              {LINGUE[target] || target}</button>)}
          </nav>
        </div>
        <label className="traduzioniServizio f6 mr2">Servizio{" "}
          <select value={servizio} disabled={busy} onChange={(event) => setServizio(event.target.value)} aria-label="Servizio di traduzione">
            {(quote?.servizi || [{ id: "auto", etichetta: "Automatico", disponibile: true }]).map((item) =>
              <option key={item.id} value={item.id} disabled={!item.disponibile}>{item.etichetta}{item.disponibile ? "" : " (non disponibile)"}</option>)}
          </select>
        </label>
        <div className="traduzioniAzioni">
          <button type="button" className={stilePulsanti.buttonClassesGreen} disabled={Boolean(motivo)}
            onClick={() => traduci("mancanti")}>Traduci in {nomeLingua}</button>
          <button type="button" className={`${buttonClass} ml2`} disabled={Boolean(motivo) || !stato || !campiConSorgente.length}
            onClick={() => setConferma(true)}>Ritraduci tutti</button>
        </div>
        <button type="button" className="traduzioniAggiorna f6 link pointer underline-hover" disabled={busy || loading}
          onClick={() => setRefresh((value) => value + 1)}>Aggiorna stato e quota</button>
      </div>
    {quote && <p className="f7 gray mv1" aria-label="Quote dei servizi">
      {partiQuota(quote.canali).map((parte) => `${parte.etichetta}: ${formatCaratteri(parte.residuo)} residui`).join(" · ")}
      {ordineCascata(quote) && ` · Ordine automatico: ${ordineCascata(quote)}`}
    </p>}
    {motivo && <p role="status">{motivo}</p>}
    {avvisoQuota && <p role="status">{avvisoQuota}</p>}
    {avviso && <p role="status">{avviso}</p>}
    {errore && <p role="alert">{errore}</p>}
    {erroreTraduzione && <p role="alert">{erroreTraduzione}</p>}
    {erroreStato && <p role="alert">{erroreStato}</p>}
    </div>
    <div className="traduzioniContenuto pa2">
    {loading && <TextSkeleton lines={2} />}
    {conferma && <div role="alertdialog" aria-label="Conferma ritraduzione" className="pa3 mb3 ba bordoColAlt">
      <p>Ritradurre in {LINGUE[selezionata]}? Questi campi verranno sovrascritti:</p>
      <ul>{campiConSorgente.map(([campo, label]) => <li key={campo}>{label}</li>)}</ul>
      <button type="button" className={buttonClass} disabled={Boolean(motivo)} onClick={() => traduci("tutti")}>Conferma ritraduzione</button>
      <button type="button" className={`${buttonClass} ml2`} onClick={() => setConferma(false)}>Annulla</button>
    </div>}
    <div className="traduzioniEditorHeading"><h3>Italiano</h3><h3>{LINGUE[selezionata]}</h3></div>
    {CAMPI.map(([campo, label]) => {
      const source = prodotto.descrizioniSuPS?.[campo]?.it || "";
      const target = prodotto.descrizioniSuPS?.[campo]?.[selezionata] || "";
      return <div key={campo} className="traduzioniEditorRow" data-campo={campo}>
        <div className="traduzioniEditorCell pa3 ba bordoColAlt">
          <h4 className="mt0">{label} · Italiano</h4>
          <TestoFormattato campo={campo} testo={source} vuoto="Nessun testo italiano." />
        </div>
        <div className="traduzioniEditorCell pa3 ba bordoColAlt">
          <h4 className="mt0">{label} · {LINGUE[selezionata]}</h4>
          {statiCampo(campo)}
          <div className="mt2"><TestoFormattato campo={campo} testo={target} vuoto="Nessuna traduzione." /></div>
        </div>
      </div>;
    })}
    </div>
  </section>;
}
